import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { AssistantAction, ChatMessage, DisplayPart } from "@/api/assistant";
import Markdown from "./Markdown";
import { DocumentCardView, ProposalCard, ToolStep } from "./Parts";

/** A display part while a reply is still streaming (tools carry live state). */
export type LivePart =
  | DisplayPart
  | { type: "live-tool"; id: string; label: string; state: "running" | "done" | "error" };

/** The brand mark next to the assistant's turns. */
export function Spark({ size = 28, spinning = false }: { size?: number; spinning?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className={spinning ? "animate-[spin_2.4s_linear_infinite]" : undefined}
    >
      <defs>
        <linearGradient id="spark-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgb(var(--azure))" />
          <stop offset="1" stopColor="rgb(var(--violet))" />
        </linearGradient>
      </defs>
      {Array.from({ length: 8 }, (_, i) => (
        <rect
          key={i}
          x="14.6"
          y="2"
          width="2.8"
          height="12"
          rx="1.4"
          fill="url(#spark-g)"
          transform={`rotate(${i * 45} 16 16)`}
        />
      ))}
    </svg>
  );
}

type Group =
  | { kind: "tools"; items: { label: string; state: "running" | "done" | "error" }[] }
  | { kind: "part"; part: LivePart };

/** Collapse runs of tool steps so a research-heavy reply stays readable. */
function group(parts: LivePart[]): Group[] {
  const out: Group[] = [];
  for (const p of parts) {
    const step =
      p.type === "tool"
        ? { label: p.label, state: (p.error ? "error" : "done") as "done" | "error" }
        : p.type === "live-tool"
          ? { label: p.label, state: p.state }
          : null;
    if (step) {
      const last = out[out.length - 1];
      if (last?.kind === "tools") last.items.push(step);
      else out.push({ kind: "tools", items: [step] });
    } else {
      out.push({ kind: "part", part: p });
    }
  }
  return out;
}

function ToolGroup({ items }: { items: { label: string; state: "running" | "done" | "error" }[] }) {
  const running = items.some((i) => i.state === "running");
  const [open, setOpen] = useState(false);
  if (items.length <= 3 || running) {
    // Short runs (and the live one) show every step; long finished runs collapse.
    const shown = running && items.length > 4 ? items.slice(-4) : items;
    return (
      <div className="space-y-1.5 py-1">
        {running && items.length > 4 && (
          <div className="text-sm text-mut-500">{items.length - 4} earlier steps</div>
        )}
        {shown.map((i, n) => (
          <ToolStep key={n} label={i.label} state={i.state} />
        ))}
      </div>
    );
  }
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div className="py-1">
      <button
        className="flex items-center gap-1.5 text-sm text-mut-500 hover:text-mut-300"
        onClick={() => setOpen((o) => !o)}
      >
        <Chevron className="w-3.5 h-3.5" />
        Worked through {items.length} steps
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 pl-5">
          {items.map((i, n) => (
            <ToolStep key={n} label={i.label} state={i.state} />
          ))}
        </div>
      )}
    </div>
  );
}

export function AssistantTurn({
  parts,
  actions,
  thinking = false,
}: {
  parts: LivePart[];
  actions: Record<string, AssistantAction>;
  thinking?: boolean;
}) {
  return (
    <div className="flex gap-3.5 animate-fade-up">
      <div className="pt-0.5 shrink-0">
        <Spark size={24} spinning={thinking} />
      </div>
      <div className="flex-1 min-w-0 space-y-3">
        {thinking && parts.length === 0 && (
          <div className="text-md text-mut-500 pt-0.5">Thinking…</div>
        )}
        {group(parts).map((g, i) =>
          g.kind === "tools" ? (
            <ToolGroup key={i} items={g.items} />
          ) : g.part.type === "text" ? (
            <Markdown key={i} text={g.part.text} />
          ) : g.part.type === "proposal" ? (
            <ProposalCard
              key={g.part.action.id}
              action={actions[g.part.action.id] ?? g.part.action}
            />
          ) : g.part.type === "document" ? (
            <DocumentCardView key={g.part.document.id} document={g.part.document} />
          ) : null,
        )}
      </div>
    </div>
  );
}

export function UserTurn({ part }: { part: DisplayPart | undefined }) {
  if (!part || part.type !== "text") return null;
  return (
    <div className="flex justify-end animate-fade-up">
      <div className="max-w-[85%] rounded-2xl rounded-br-md bg-ink-800 px-4 py-2.5 text-base whitespace-pre-wrap break-words">
        {part.skill && (
          <span className="inline-block mb-1 mr-1.5 px-2 py-0.5 rounded-md bg-accent-violet/10 text-accent-violet text-sm font-medium">
            /{part.skill}
          </span>
        )}
        {part.text}
      </div>
    </div>
  );
}

export function Turn({
  message,
  actions,
}: {
  message: ChatMessage;
  actions: Record<string, AssistantAction>;
}) {
  return message.role === "user" ? (
    <UserTurn part={message.display[0]} />
  ) : (
    <AssistantTurn parts={message.display} actions={actions} />
  );
}
